import { logger } from "@/lib/logger";
import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  DollarSign, Users, TrendingUp, Link2, Copy, CheckCircle,
  LogOut, Loader2, Clock, Eye, Shield, BarChart3, ExternalLink,
} from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/affiliates`;

function authHeaders() {
  const t = localStorage.getItem("aff_session_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
}

// ==================== LOGIN ====================
function AffLogin({ onAuth }) {
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "", website: "", channel: "" });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const endpoint = mode === "login" ? "auth/login" : "auth/signup";
      const body = mode === "login" ? { email: form.email, password: form.password } : form;
      const resp = await fetch(`${API}/${endpoint}`, {
        method: "POST", credentials: "include",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await resp.json();
      if (resp.ok) {
        localStorage.setItem("aff_session_token", data.token);
        onAuth(data);
      } else {
        toast.error(data.detail || "Failed");
      }
    } catch { toast.error("Connection error"); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-14 h-14 rounded-2xl bg-emerald-500/20 flex items-center justify-center mx-auto mb-4">
            <DollarSign className="w-7 h-7 text-emerald-400" />
          </div>
          <h1 className="font-heading text-3xl text-white font-bold">Affiliate Portal</h1>
          <p className="text-zinc-400 mt-2">Earn 20% recurring commissions</p>
        </div>
        <Card className="bg-[#121212] border-white/10">
          <CardContent className="p-6 space-y-4">
            <div className="flex gap-2 mb-4">
              <Button onClick={() => setMode("login")} variant={mode === "login" ? "default" : "ghost"} className={mode === "login" ? "flex-1 bg-white text-black" : "flex-1 text-zinc-400"}>Sign In</Button>
              <Button onClick={() => setMode("signup")} variant={mode === "signup" ? "default" : "ghost"} className={mode === "signup" ? "flex-1 bg-white text-black" : "flex-1 text-zinc-400"}>Apply</Button>
            </div>
            {mode === "signup" && (
              <>
                <div className="space-y-2">
                  <Label className="text-zinc-400">Name</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" className="bg-black/50 border-white/10 text-white" />
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-400">Website / Channel</Label>
                  <Input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="https://yoursite.com" className="bg-black/50 border-white/10 text-white" />
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-400">How will you promote?</Label>
                  <Input value={form.channel} onChange={(e) => setForm({ ...form, channel: e.target.value })} placeholder="Blog, YouTube, Email list, etc." className="bg-black/50 border-white/10 text-white" />
                </div>
              </>
            )}
            <div className="space-y-2">
              <Label className="text-zinc-400">Email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@company.com" className="bg-black/50 border-white/10 text-white" />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400">Password</Label>
              <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Password" className="bg-black/50 border-white/10 text-white" onKeyDown={(e) => e.key === "Enter" && handleSubmit()} />
            </div>
            <Button onClick={handleSubmit} disabled={loading} className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 text-white h-11">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {mode === "login" ? "Sign In" : "Apply to Join"}
            </Button>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}

// ==================== PENDING ====================
function AffPending({ user, onLogout }) {
  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4">
      <div className="text-center max-w-md">
        <Clock className="w-12 h-12 text-amber-400 mx-auto mb-4" />
        <h2 className="text-white text-xl font-bold mb-2">Application Under Review</h2>
        <p className="text-zinc-400 mb-4">Your affiliate application ({user.email}) is being reviewed. You'll receive approval within 24 hours.</p>
        <p className="text-zinc-500 text-sm mb-6">Ref code: <span className="text-emerald-400 font-mono">{user.ref_code}</span></p>
        <Button onClick={onLogout} variant="outline" className="border-white/20 text-white"><LogOut className="w-4 h-4 mr-2" /> Log Out</Button>
      </div>
    </div>
  );
}

// ==================== DASHBOARD ====================
function AffDashboard({ user, onLogout }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAdmin, setShowAdmin] = useState(false);
  const [affiliates, setAffiliates] = useState([]);
  const [copied, setCopied] = useState(false);
  const [connectLoading, setConnectLoading] = useState(false);
  const isAdmin = user.email === "matgeo.2008@gmail.com";

  const fetchDashboard = useCallback(async () => {
    try {
      const resp = await fetch(`${API}/dashboard`, { credentials: "include", headers: authHeaders() });
      if (resp.ok) setData(await resp.json());
    } catch (e) { logger.error("Error:", e); } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  const fetchAffiliates = async () => {
    try {
      const resp = await fetch(`${API}/admin/list`, { credentials: "include", headers: authHeaders() });
      if (resp.ok) setAffiliates(await resp.json());
    } catch (e) { logger.error("Error:", e); }
  };

  const handleAdminAction = async (id, action) => {
    await fetch(`${API}/admin/${action}/${id}`, { method: "POST", credentials: "include", headers: authHeaders() });
    toast.success(`Affiliate ${action}d`);
    fetchAffiliates();
  };

  const handlePayout = async (id) => {
    const resp = await fetch(`${API}/admin/payout/${id}`, { method: "POST", credentials: "include", headers: authHeaders() });
    if (resp.ok) { const d = await resp.json(); toast.success(`Paid $${d.amount_paid}`); fetchAffiliates(); }
    else toast.error("Payout failed");
  };

  const copyLink = () => {
    navigator.clipboard.writeText(data?.ref_link || "");
    setCopied(true);
    toast.success("Referral link copied!");
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSetupStripeConnect = async () => {
    setConnectLoading(true);
    try {
      // Create account if needed
      if (!data?.stripe_connected) {
        await fetch(`${API}/connect/create`, { method: "POST", credentials: "include", headers: authHeaders() });
      }
      // Get onboarding link
      const resp = await fetch(`${API}/connect/onboarding-link`, { method: "POST", credentials: "include", headers: authHeaders() });
      if (resp.ok) {
        const d = await resp.json();
        window.open(d.url, "_blank");
      } else {
        toast.error("Failed to create onboarding link");
      }
    } catch { toast.error("Connection error"); }
    finally { setConnectLoading(false); }
  };

  if (loading) return <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center"><Loader2 className="w-8 h-8 text-emerald-500 animate-spin" /></div>;

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <div className="border-b border-white/5 px-6 py-3">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <DollarSign className="w-5 h-5 text-emerald-400" />
            <span className="font-heading text-lg text-white font-bold">Affiliate Portal</span>
          </div>
          <div className="flex items-center gap-3">
            {isAdmin && <Button size="sm" variant="ghost" onClick={() => { setShowAdmin(!showAdmin); if (!showAdmin) fetchAffiliates(); }} className="text-zinc-400"><Users className="w-4 h-4 mr-1" /> Admin</Button>}
            <span className="text-zinc-400 text-sm">{user.email}</span>
            <Button size="sm" variant="ghost" onClick={onLogout} className="text-zinc-400"><LogOut className="w-4 h-4" /></Button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-6">
        {/* Ref Link */}
        <div className="bg-[#121212] border border-emerald-500/20 rounded-xl p-4 mb-6">
          <p className="text-zinc-400 text-xs mb-2">Your Referral Link</p>
          <div className="flex items-center gap-2">
            <code className="flex-1 bg-black/50 text-emerald-400 px-3 py-2 rounded-lg text-sm font-mono overflow-x-auto">{data?.ref_link}</code>
            <Button onClick={copyLink} size="sm" className={copied ? "bg-emerald-600 text-white" : "bg-white/10 text-white hover:bg-white/20"}>
              {copied ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            </Button>
          </div>
        </div>

        {/* Stripe Connect */}
        <div className={cn("border rounded-xl p-4 mb-6", data?.stripe_onboarding_complete ? "bg-[#121212] border-emerald-500/20" : "bg-[#121212] border-amber-500/20")}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white font-semibold text-sm flex items-center gap-2">
                {data?.stripe_onboarding_complete ? <CheckCircle className="w-4 h-4 text-emerald-400" /> : <Clock className="w-4 h-4 text-amber-400" />}
                {data?.stripe_onboarding_complete ? "Stripe Payouts Connected" : "Set Up Automatic Payouts"}
              </p>
              <p className="text-zinc-400 text-xs mt-0.5">
                {data?.stripe_onboarding_complete
                  ? "Commissions will be paid directly to your bank account via Stripe"
                  : "Connect your bank account via Stripe to receive automatic payouts"}
              </p>
            </div>
            {!data?.stripe_onboarding_complete && (
              <Button onClick={handleSetupStripeConnect} disabled={connectLoading} size="sm" className="bg-violet-600 text-white hover:bg-violet-700">
                {connectLoading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <ExternalLink className="w-4 h-4 mr-1" />}
                {data?.stripe_connected ? "Complete Setup" : "Connect Stripe"}
              </Button>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {[
            { label: "Total Clicks", value: data?.total_clicks || 0, icon: Eye, color: "text-blue-400" },
            { label: "Conversions", value: data?.total_conversions || 0, icon: TrendingUp, color: "text-emerald-400" },
            { label: "Total Earnings", value: `$${(data?.total_earnings || 0).toFixed(2)}`, icon: DollarSign, color: "text-amber-400" },
            { label: "Pending Payout", value: `$${(data?.pending_payout || 0).toFixed(2)}`, icon: Clock, color: "text-violet-400" },
          ].map((s) => {
            const Icon = s.icon;
            return (
              <Card key={s.label} className="bg-[#121212] border-white/10">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-zinc-400 text-xs">{s.label}</span>
                    <Icon className={cn("w-4 h-4", s.color)} />
                  </div>
                  <p className="text-white text-xl font-bold">{s.value}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Info */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-[#121212] border border-white/10 rounded-xl p-4">
            <p className="text-zinc-400 text-xs">Commission Rate</p>
            <p className="text-white text-lg font-bold">{data?.commission_rate}</p>
          </div>
          <div className="bg-[#121212] border border-white/10 rounded-xl p-4">
            <p className="text-zinc-400 text-xs">Cookie Window</p>
            <p className="text-white text-lg font-bold">{data?.cookie_days} days</p>
          </div>
          <div className="bg-[#121212] border border-white/10 rounded-xl p-4">
            <p className="text-zinc-400 text-xs">Paid Out</p>
            <p className="text-white text-lg font-bold">${(data?.paid_out || 0).toFixed(2)}</p>
          </div>
        </div>

        {/* Conversions */}
        <Card className="bg-[#121212] border-white/10 mb-6">
          <CardHeader><CardTitle className="text-white text-base">Recent Conversions</CardTitle></CardHeader>
          <CardContent>
            {(data?.conversions || []).length === 0 ? (
              <p className="text-zinc-500 text-sm text-center py-4">No conversions yet. Share your referral link to start earning.</p>
            ) : (
              <div className="space-y-2">
                {data.conversions.map((c) => (
                  <div key={c.conversion_id} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                    <div>
                      <p className="text-zinc-300 text-sm">{c.customer_email || "Customer"}</p>
                      <p className="text-zinc-500 text-xs">{c.plan_id} — {new Date(c.created_at).toLocaleDateString()}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-emerald-400 font-bold">${c.commission?.toFixed(2)}</p>
                      <Badge className={c.status === "paid" ? "bg-emerald-500/10 text-emerald-400 text-xs" : "bg-amber-500/10 text-amber-400 text-xs"}>{c.status}</Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Admin Panel */}
        {showAdmin && isAdmin && (
          <Card className="bg-[#121212] border-white/10">
            <CardHeader><CardTitle className="text-white text-base flex items-center gap-2"><Shield className="w-4 h-4 text-violet-400" /> Affiliate Management</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-2">
                {affiliates.map((a) => (
                  <div key={a.affiliate_id} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                    <div>
                      <p className="text-white text-sm font-medium">{a.name} <span className="text-zinc-500">({a.email})</span></p>
                      <p className="text-zinc-500 text-xs">Code: {a.ref_code} | Clicks: {a.total_clicks} | Conv: {a.total_conversions} | Earned: ${(a.total_earnings || 0).toFixed(2)} | Pending: ${(a.pending_payout || 0).toFixed(2)}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={a.status === "approved" ? "bg-emerald-500/10 text-emerald-400" : a.status === "rejected" ? "bg-red-500/10 text-red-400" : "bg-amber-500/10 text-amber-400"}>{a.status}</Badge>
                      {a.status === "pending" && <Button size="sm" onClick={() => handleAdminAction(a.affiliate_id, "approve")} className="bg-emerald-600 text-white text-xs">Approve</Button>}
                      {a.status === "approved" && a.pending_payout > 0 && <Button size="sm" onClick={() => handlePayout(a.affiliate_id)} className="bg-violet-600 text-white text-xs">Pay ${(a.pending_payout || 0).toFixed(2)}</Button>}
                      {a.status === "approved" && a.email !== "matgeo.2008@gmail.com" && <Button size="sm" variant="outline" onClick={() => handleAdminAction(a.affiliate_id, "reject")} className="border-red-500/30 text-red-400 text-xs">Revoke</Button>}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

// ==================== MAIN ====================
export default function AffiliatePortalPage() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const resp = await fetch(`${API}/auth/me`, { credentials: "include", headers: authHeaders() });
      if (resp.ok) setUser(await resp.json());
    } catch (e) { logger.error("Error:", e); } finally { setChecking(false); }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const handleLogout = async () => {
    await fetch(`${API}/auth/logout`, { method: "POST", credentials: "include", headers: authHeaders() });
    localStorage.removeItem("aff_session_token");
    setUser(null);
  };

  if (checking) return <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center"><Loader2 className="w-8 h-8 text-emerald-500 animate-spin" /></div>;
  if (!user) return <AffLogin onAuth={(u) => setUser(u)} />;
  if (user.status === "pending") return <AffPending user={user} onLogout={handleLogout} />;
  return <AffDashboard user={user} onLogout={handleLogout} />;
}
